/**
 * ReportIssueModal — iter87.105
 *
 * Simple two-step flow:
 *   1) Pick a category → we surface 0-3 matching FAQ entries as deflection
 *   2) "Yes, that answered it" (deflect, logs a metric) OR "No, keep going"
 *      → title + description form → submit → tracking ID toast
 *
 * Category-only tagging. No screenshot upload. No severity picker. Auto-
 * captures page URL + browser UA + league_id server-side. Fires from the
 * admin-only "Report an Issue" card in League Settings.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, ChevronRight, ArrowLeft, Send, Check } from "lucide-react";

const CATEGORIES = [
    { slug: "cant_find",        label: "Can't find something",      hint: "Nav / permissions / limits" },
    { slug: "feature_broken",   label: "Feature isn't working",     hint: "Something's actually broken" },
    { slug: "data_wrong",       label: "Data looks wrong",          hint: "Scores / standings / picks" },
    { slug: "subscription",     label: "Subscription or access",    hint: "Pro+, Trial, tier questions" },
    { slug: "suggestion",       label: "Feature idea / suggestion", hint: "Something new to build" },
    { slug: "other",            label: "Something else",            hint: "Anything not above" },
];

/**
 * iter87.188 — `apiClient` is now injectable so surfaces with their own
 * axios client + auth chain (e.g. the Rivalry Pro+ shell using
 * `proApi`) can reuse this modal verbatim. Default stays the legacy
 * `api` client to preserve every existing call site (Dashboard drawer,
 * Site Admin, etc.). The Pro+ shell passes its own client, which
 * automatically attaches the Pro+ Authorization bearer — the
 * `/api/issues` handler already accepts that token as of iter87.188.
 */
export default function ReportIssueModal({ open, onClose, leagueId, apiClient = api }) {
    const [step, setStep] = useState("category"); // category | deflect | form | success
    const [category, setCategory] = useState(null);
    const [faqEntries, setFaqEntries] = useState([]);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const [trackingId, setTrackingId] = useState(null);

    useEffect(() => {
        if (!open) {
            // reset on close
            setStep("category");
            setCategory(null);
            setFaqEntries([]);
            setTitle("");
            setDescription("");
            setTrackingId(null);
        }
    }, [open]);

    if (!open) return null;

    const pickCategory = async (cat) => {
        setCategory(cat);
        setBusy(true);
        try {
            const res = await apiClient.getFaqByCategory(cat.slug);
            setFaqEntries(res?.entries || []);
            setStep("deflect");
        } catch (err) {
            // If FAQ fetch fails, skip straight to form — don't block the report
            setFaqEntries([]);
            setStep("form");
        }
        setBusy(false);
    };

    const submitDeflected = async () => {
        setBusy(true);
        try {
            await apiClient.createIssue({
                category: category.slug,
                title: `[deflected] ${category.label}`,
                description: "User self-served via FAQ.",
                league_id: leagueId || null,
                page_url: window.location.pathname,
                deflected: true,
            });
            toast.success("Great — glad we could help.");
            onClose?.();
        } catch (err) {
            // still close on failure — deflection metric is nice-to-have, not blocking
            onClose?.();
        }
        setBusy(false);
    };

    const submitReport = async () => {
        if (!title.trim() || !description.trim()) {
            toast.error("Please add a title and description.");
            return;
        }
        setBusy(true);
        try {
            const res = await apiClient.createIssue({
                category: category.slug,
                title: title.trim(),
                description: description.trim(),
                league_id: leagueId || null,
                page_url: window.location.pathname,
                deflected: false,
            });
            setTrackingId(res?.tracking_id || "SUBMITTED");
            setStep("success");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
        }
        setBusy(false);
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="report-issue-modal"
        >
            <div className="brutal-card mt-8 mb-8 w-full max-w-lg p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-display font-black uppercase text-lg" data-testid="report-issue-modal-title">
                            {step === "category" && "Report an Issue"}
                            {step === "deflect" && "Quick check first"}
                            {step === "form" && `Report: ${category?.label}`}
                            {step === "success" && "Thanks — we got it"}
                        </h3>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                            {step === "category" && "Pick the category that best describes what's happening."}
                            {step === "deflect" && `${faqEntries.length} common answer${faqEntries.length === 1 ? "" : "s"} for "${category?.label}"`}
                            {step === "form" && "Keep it short — Site Admin will follow up."}
                            {step === "success" && `Tracking ID: ${trackingId}`}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-ghost p-2"
                        data-testid="report-issue-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {step === "category" && (
                    <div className="space-y-2" data-testid="report-issue-category-picker">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.slug}
                                type="button"
                                onClick={() => pickCategory(c)}
                                disabled={busy}
                                className="brutal-card-light w-full p-3 text-left flex items-center justify-between gap-3 hover:border-cyber transition-colors disabled:opacity-40"
                                data-testid={`report-issue-category-${c.slug}`}
                            >
                                <div>
                                    <div className="text-xs font-bold text-white">{c.label}</div>
                                    <div className="text-[10px] text-zinc-500 mt-0.5">{c.hint}</div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                            </button>
                        ))}
                    </div>
                )}

                {step === "deflect" && (
                    <div className="space-y-3" data-testid="report-issue-deflect">
                        {faqEntries.length === 0 ? (
                            <p className="text-xs text-zinc-500 italic">
                                No matching FAQ entries yet — jump straight to filing.
                            </p>
                        ) : (
                            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                {faqEntries.map((entry, i) => (
                                    <details
                                        key={i}
                                        className="brutal-card-light p-3 cursor-pointer"
                                        data-testid={`report-issue-faq-entry-${i}`}
                                    >
                                        <summary className="text-xs font-bold text-white cursor-pointer">
                                            {entry.title}
                                        </summary>
                                        <p className="text-[11px] text-zinc-300 mt-2 leading-relaxed whitespace-pre-wrap">
                                            {entry.body}
                                        </p>
                                    </details>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2 pt-2 border-t border-[#222]">
                            <button
                                type="button"
                                onClick={() => setStep("category")}
                                disabled={busy}
                                className="brutal-btn-ghost text-xs py-2 px-3 disabled:opacity-40"
                                data-testid="report-issue-back-to-category"
                            >
                                <ArrowLeft className="w-3 h-3" /> Back
                            </button>
                            {faqEntries.length > 0 && (
                                <button
                                    type="button"
                                    onClick={submitDeflected}
                                    disabled={busy}
                                    className="brutal-btn text-xs py-2 px-3 border-green-500/60 text-green-400 disabled:opacity-40 flex-1"
                                    data-testid="report-issue-deflect-yes"
                                >
                                    <Check className="w-3 h-3" /> Yes, that helped
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setStep("form")}
                                disabled={busy}
                                className="brutal-btn text-xs py-2 px-3 border-cyber text-cyber disabled:opacity-40 flex-1"
                                data-testid="report-issue-deflect-no"
                            >
                                No, file it <ChevronRight className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                )}

                {step === "form" && (
                    <div className="space-y-3" data-testid="report-issue-form">
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1">Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={200}
                                placeholder="One-line summary"
                                className="w-full bg-[#0d0d0d] border border-[#222] rounded px-3 py-2 text-xs focus:border-cyber focus:outline-none text-white"
                                data-testid="report-issue-title"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1">Description</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={4000}
                                rows={6}
                                placeholder="What happened? What did you expect? What did you see instead?"
                                className="w-full bg-[#0d0d0d] border border-[#222] rounded px-3 py-2 text-xs focus:border-cyber focus:outline-none text-white leading-relaxed"
                                data-testid="report-issue-description"
                            />
                            <div className="text-right text-[9px] text-zinc-600 mt-0.5">
                                {description.length} / 4000
                            </div>
                        </div>
                        <p className="text-[10px] text-zinc-500 italic">
                            We'll auto-capture the current page URL and your browser info to help troubleshoot.
                        </p>
                        <div className="flex gap-2 pt-2 border-t border-[#222]">
                            <button
                                type="button"
                                onClick={() => setStep("deflect")}
                                disabled={busy}
                                className="brutal-btn-ghost text-xs py-2 px-3 disabled:opacity-40"
                                data-testid="report-issue-back-to-deflect"
                            >
                                <ArrowLeft className="w-3 h-3" /> Back
                            </button>
                            <button
                                type="button"
                                onClick={submitReport}
                                disabled={busy || !title.trim() || !description.trim()}
                                className="brutal-btn text-xs py-2 px-3 border-cyber text-cyber disabled:opacity-40 flex-1"
                                data-testid="report-issue-submit"
                            >
                                <Send className="w-3 h-3" /> {busy ? "Sending…" : "Submit"}
                            </button>
                        </div>
                    </div>
                )}

                {step === "success" && (
                    <div className="space-y-3 text-center" data-testid="report-issue-success">
                        <div className="w-12 h-12 mx-auto bg-green-400 border-2 border-black shadow-brutal flex items-center justify-center">
                            <Check className="w-6 h-6 text-black" strokeWidth={2.5} />
                        </div>
                        <p className="text-xs text-zinc-300 leading-relaxed">
                            Thanks — Site Admin will review and follow up if we need more info.
                        </p>
                        <p className="text-[10px] text-zinc-500">
                            Tracking ID: <span className="font-mono text-cyber">{trackingId}</span>
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="brutal-btn text-xs py-2 px-4"
                            data-testid="report-issue-done"
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}
